import React, { useState, useEffect, useRef } from 'react';
import { Smartphone, RefreshCw, Loader, CheckCircle, Clock, ShieldCheck, Pencil, X, Check } from 'lucide-react';
import {
    sendPhoneVerificationCode,
    validatePhoneCode,
    changeUserPhone
} from '../../../services/verificationService';

const RESEND_COOLDOWN = 60; // seconds
const CODE_LENGTH = 4;

const PhoneVerification = ({ userId, userPhone, onVerified }) => {
    const [code, setCode] = useState(Array(CODE_LENGTH).fill(''));
    const [loading, setLoading] = useState(true);
    const [validating, setValidating] = useState(false);
    const [sendingCode, setSendingCode] = useState(false);
    const [resendCooldown, setResendCooldown] = useState(0);
    const [error, setError] = useState('');
    const [codeSent, setCodeSent] = useState(false);
    const [verified, setVerified] = useState(false);
    const [notificationId, setNotificationId] = useState(null);
    const inputRefs = useRef([]);
    const cooldownRef = useRef(null);

    // ── Phone edit state ──
    const [editing, setEditing] = useState(false);
    const [newPhone, setNewPhone] = useState('');
    const [phoneError, setPhoneError] = useState('');
    const [changingPhone, setChangingPhone] = useState(false);
    const [currentPhone, setCurrentPhone] = useState(userPhone || '');

    // Mostrar el número COMPLETO con prefijo (no enmascarado).
    // Si el SMS no llega, el usuario necesita poder ver el número exacto que está en
    // el sistema para confirmar si es el correcto o necesita actualizarlo.
    const displayPhone = (phone) => {
        if (!phone) return '';
        const clean = String(phone).replace(/\D/g, '');
        // Heurística: si tiene 10 dígitos asumimos México (+52). Si tiene 11+ asumimos
        // que ya incluye prefijo internacional.
        if (clean.length === 10) return `+52 ${clean.slice(0, 2)} ${clean.slice(2, 6)} ${clean.slice(6)}`;
        if (clean.length > 10) return `+${clean.slice(0, clean.length - 10)} ${clean.slice(-10)}`;
        return clean;
    };

    // Helper: extrae notificationId de la response (acepta varias formas).
    const extractNotificationId = (resp) => {
        const data = resp?.data || {};
        return data?.data?.notification_id || data?.notification_id || null;
    };

    // Send initial code
    useEffect(() => {
        const sendInitialCode = async () => {
            setLoading(true);
            setError('');
            try {
                const result = await sendPhoneVerificationCode(userId);
                if (result.success) {
                    setNotificationId(extractNotificationId(result));
                    setCodeSent(true);
                    setResendCooldown(RESEND_COOLDOWN);
                } else {
                    const errMsg = result.error || 'Error al enviar el código. Intenta de nuevo.';
                    setError(errMsg);
                    // Auto-open edit form if the stored number is invalid
                    if (errMsg.toLowerCase().includes('invalid number') || errMsg.toLowerCase().includes('invalid')) {
                        setEditing(true);
                        setError('El número guardado no es válido. Por favor actualiza tu número de teléfono.');
                    }
                }
            } catch {
                setError('Error de conexión. Intenta de nuevo.');
            } finally {
                setLoading(false);
            }
        };

        sendInitialCode();
    }, [userId]);

    // Resend cooldown timer
    useEffect(() => {
        if (resendCooldown <= 0) {
            if (cooldownRef.current) clearInterval(cooldownRef.current);
            return;
        }

        cooldownRef.current = setInterval(() => {
            setResendCooldown(prev => {
                if (prev <= 1) {
                    clearInterval(cooldownRef.current);
                    return 0;
                }
                return prev - 1;
            });
        }, 1000);

        return () => {
            if (cooldownRef.current) clearInterval(cooldownRef.current);
        };
    }, [resendCooldown]);

    // Handle digit input
    const handleDigitChange = (index, value) => {
        if (!/^\d*$/.test(value)) return;

        const newCode = [...code];
        newCode[index] = value.slice(-1);
        setCode(newCode);
        setError('');

        if (value && index < CODE_LENGTH - 1) {
            inputRefs.current[index + 1]?.focus();
        }

        const fullCode = newCode.join('');
        if (fullCode.length === CODE_LENGTH && newCode.every(d => d !== '')) {
            handleValidate(fullCode);
        }
    };

    // Handle backspace
    const handleKeyDown = (index, e) => {
        if (e.key === 'Backspace' && !code[index] && index > 0) {
            inputRefs.current[index - 1]?.focus();
        }
    };

    // Handle paste
    const handlePaste = (e) => {
        e.preventDefault();
        const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, CODE_LENGTH);
        if (pasted.length > 0) {
            const newCode = Array(CODE_LENGTH).fill('');
            for (let i = 0; i < pasted.length; i++) {
                newCode[i] = pasted[i];
            }
            setCode(newCode);

            const focusIdx = Math.min(pasted.length, CODE_LENGTH - 1);
            inputRefs.current[focusIdx]?.focus();

            if (pasted.length === CODE_LENGTH) {
                handleValidate(pasted);
            }
        }
    };

    // Validate the code
    const handleValidate = async (codeStr) => {
        const fullCode = codeStr || code.join('');
        if (fullCode.length !== CODE_LENGTH) {
            setError('Ingresa el código completo');
            return;
        }

        setValidating(true);
        setError('');

        try {
            const result = await validatePhoneCode(userId, fullCode, notificationId);
            if (result.success) {
                setVerified(true);
                setTimeout(() => {
                    if (onVerified) onVerified();
                }, 1500);
            } else {
                setError(result.error || 'Código incorrecto. Intenta de nuevo.');
                setCode(Array(CODE_LENGTH).fill(''));
                inputRefs.current[0]?.focus();
            }
        } catch {
            setError('Error al verificar. Intenta de nuevo.');
        } finally {
            setValidating(false);
        }
    };

    // Resend code
    const handleResend = async () => {
        if (resendCooldown > 0 || sendingCode) return;
        setSendingCode(true);
        setError('');
        setCode(Array(CODE_LENGTH).fill(''));

        try {
            const result = await sendPhoneVerificationCode(userId);
            if (result.success) {
                setNotificationId(extractNotificationId(result));
                setCodeSent(true);
                setResendCooldown(RESEND_COOLDOWN);
            } else {
                setError(result.error || 'Error al reenviar el código');
            }
        } catch {
            setError('Error de conexión. Intenta de nuevo.');
        } finally {
            setSendingCode(false);
        }
    };

    // ── Change phone flow (mirrors FlutterFlow) ──
    // 1. Validate form → 2. POST change_user_mobile → 3. If success → send code
    const handleStartEdit = () => {
        setEditing(true);
        setNewPhone('');
        setPhoneError('');
    };

    const handleCancelEdit = () => {
        setEditing(false);
        setNewPhone('');
        setPhoneError('');
    };

    const handleChangePhone = async () => {
        // Step 1: Validate form
        const clean = newPhone.replace(/\D/g, '');
        if (!/^\d{10}$/.test(clean)) {
            setPhoneError('Ingresa un número de 10 dígitos válido');
            return;
        }

        setChangingPhone(true);
        setPhoneError('');

        try {
            // Step 2: POST Change User Phone
            const changeResult = await changeUserPhone(userId, clean);

            // Conditional 1: ChangeUserPhone → Succeeded
            if (changeResult.success) {
                // Conditional 2: $.result.success == $.true
                // TRUE → navigate to VerificationCodePage (send new code)
                setCurrentPhone(clean);
                setEditing(false);
                setCode(Array(CODE_LENGTH).fill(''));
                setError('');

                // Send verification code to the new number
                const codeResult = await sendPhoneVerificationCode(userId);
                if (codeResult.success) {
                    setNotificationId(extractNotificationId(codeResult));
                    setCodeSent(true);
                    setResendCooldown(RESEND_COOLDOWN);
                } else {
                    setError(codeResult.error || 'Error al enviar el código al nuevo número');
                }
            } else {
                // FALSE → Show Snack Bar
                setPhoneError(changeResult.error || 'No se pudo actualizar el número. Intenta de nuevo.');
            }
        } catch {
            // FALSE → Show Snack Bar
            setPhoneError('Error de conexión. Intenta de nuevo.');
        } finally {
            setChangingPhone(false);
        }
    };

    if (loading) {
        return (
            <div className="flex flex-col items-center justify-center py-16">
                <Loader className="animate-spin text-daisies mb-4" size={40} />
                <p className="text-text-body font-medium">Enviando código de verificación...</p>
            </div>
        );
    }

    if (verified) {
        return (
            <div className="flex flex-col items-center justify-center py-16 animate-fadeIn">
                <div className="w-20 h-20 bg-mint-pill rounded-full flex items-center justify-center mb-6">
                    <CheckCircle className="text-mint-deep" size={48} />
                </div>
                <h3 className="text-2xl font-bold text-text-primary mb-2">¡Teléfono verificado!</h3>
                <p className="text-text-body">Tu número de teléfono ha sido verificado exitosamente.</p>
            </div>
        );
    }

    return (
        <div className="animate-fadeIn">
            {/* Header */}
            <div className="text-center mb-8">
                <div className="w-20 h-20 bg-daisies/10 rounded-full flex items-center justify-center mx-auto mb-6">
                    <Smartphone className="text-daisies" size={40} />
                </div>
                <h3 className="text-2xl font-bold text-text-primary mb-2">Verifica tu número de teléfono</h3>
                <p className="text-text-body">
                    Hemos enviado un código de verificación por WhatsApp/SMS a
                </p>

                {/* Phone display + edit toggle.
                    Mostramos el número completo (no enmascarado) para que el usuario
                    pueda verificar fácilmente si es el correcto. Si el código no llega
                    (provider WhatsApp falla, número equivocado), la edición está a
                    un click de distancia. */}
                {!editing ? (
                    <div className="flex flex-col items-center gap-1 mt-2">
                        <div className="flex items-center justify-center gap-2">
                            <p className="text-daisies font-bold text-xl tracking-wide">{displayPhone(currentPhone)}</p>
                            <button
                                onClick={handleStartEdit}
                                className="text-daisies/60 hover:text-daisies transition-colors p-1.5 rounded-lg hover:bg-daisies/5"
                                title="Cambiar número"
                            >
                                <Pencil size={16} />
                            </button>
                        </div>
                        <button
                            onClick={handleStartEdit}
                            className="text-xs text-text-mute hover:text-daisies underline transition-colors"
                        >
                            ¿No es tu número? Cámbialo
                        </button>
                    </div>
                ) : (
                    /* Inline phone-edit form */
                    <div className="mt-4 bg-warm-canvas rounded-xl p-4 max-w-xs mx-auto">
                        <p className="text-sm font-semibold text-text-strong mb-2">Nuevo número de teléfono</p>
                        <div className="flex gap-2">
                            <div className="flex items-center bg-white rounded-lg px-3 text-sm text-text-mute flex-shrink-0">
                                +52
                            </div>
                            <input
                                type="tel"
                                inputMode="numeric"
                                maxLength={10}
                                value={newPhone}
                                onChange={(e) => {
                                    const val = e.target.value.replace(/\D/g, '');
                                    setNewPhone(val);
                                    setPhoneError('');
                                }}
                                placeholder="10 dígitos"
                                disabled={changingPhone}
                                className="flex-1 px-3 py-2 bg-white rounded-lg text-base focus:ring-2 focus:ring-brand outline-none transition-all disabled:opacity-50"
                                autoFocus
                            />
                        </div>
                        {phoneError && (
                            <p className="text-xs text-red-600 mt-1">{phoneError}</p>
                        )}
                        <div className="flex gap-2 mt-3">
                            <button
                                onClick={handleCancelEdit}
                                disabled={changingPhone}
                                className="flex-1 py-2 px-3 border-[1.5px] border-brand text-daisies rounded-lg text-sm font-medium hover:bg-daisies/5 transition-all flex items-center justify-center gap-1 disabled:opacity-50"
                            >
                                <X size={14} />
                                Cancelar
                            </button>
                            <button
                                onClick={handleChangePhone}
                                disabled={changingPhone || newPhone.length < 10}
                                className="flex-1 py-2 px-3 bg-daisies text-white rounded-lg text-sm font-medium hover:bg-daisies-hover transition-all flex items-center justify-center gap-1 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                {changingPhone ? (
                                    <>
                                        <Loader className="animate-spin" size={14} />
                                        Cambiando...
                                    </>
                                ) : (
                                    <>
                                        <Check size={14} />
                                        Actualizar
                                    </>
                                )}
                            </button>
                        </div>
                    </div>
                )}
            </div>

            {/* Code Input */}
            <div className="mb-6">
                <p className="text-sm text-text-body text-center mb-4">Ingresa el código de 4 dígitos</p>
                <div className="flex justify-center gap-3">
                    {code.map((digit, index) => (
                        <input
                            key={index}
                            ref={el => inputRefs.current[index] = el}
                            type="text"
                            inputMode="numeric"
                            maxLength={1}
                            value={digit}
                            onChange={(e) => handleDigitChange(index, e.target.value)}
                            onKeyDown={(e) => handleKeyDown(index, e)}
                            onPaste={index === 0 ? handlePaste : undefined}
                            disabled={validating || editing}
                            className={`w-16 h-16 text-center text-2xl font-bold rounded-xl transition-all outline-none
                ${error ? 'ring-2 ring-red-400 bg-red-50' : 'bg-warm-canvas focus:ring-2 focus:ring-brand'}
                ${validating || editing ? 'opacity-50 cursor-not-allowed' : ''}
              `}
                        />
                    ))}
                </div>
            </div>

            {/* Verify button */}
            <button
                onClick={() => handleValidate()}
                disabled={validating || code.some(d => d === '') || editing}
                className="w-full py-4 bg-daisies text-white rounded-card font-bold text-lg hover:bg-daisies-hover transition-all flex items-center justify-center gap-3 mb-4 disabled:opacity-50 disabled:cursor-not-allowed"
            >
                {validating ? (
                    <>
                        <Loader className="animate-spin" size={22} />
                        Verificando...
                    </>
                ) : (
                    <>
                        <ShieldCheck size={22} />
                        Verificar código
                    </>
                )}
            </button>

            {/* Error */}
            {error && (
                <div className="bg-red-50 rounded-xl p-4 mb-4">
                    <p className="text-sm text-red-700 text-center">{error}</p>
                </div>
            )}

            {/* Resend */}
            <div className="text-center mt-6">
                <p className="text-sm text-text-mute mb-3">¿No recibiste el código?</p>
                <button
                    onClick={handleResend}
                    disabled={resendCooldown > 0 || sendingCode}
                    className="inline-flex items-center gap-2 px-6 py-3 border-[1.5px] border-brand text-daisies rounded-card font-semibold hover:bg-daisies/5 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                >
                    {sendingCode ? (
                        <>
                            <Loader className="animate-spin" size={16} />
                            Enviando...
                        </>
                    ) : resendCooldown > 0 ? (
                        <>
                            <Clock size={16} />
                            Reenviar en {resendCooldown}s
                        </>
                    ) : (
                        <>
                            <RefreshCw size={16} />
                            Reenviar código
                        </>
                    )}
                </button>
            </div>
        </div>
    );
};

export default PhoneVerification;
